import { z } from "zod/v4";

import { JSONValueSchema } from "../common";
import { AiNodeContextSchema } from "../context-schemas";
import { BaseNodeExecutionResultSchema } from "../node-execution-result";

import {
  BaseNodeDefinitionConfigSchema,
  BaseNodeDefinitionSchema,
} from "./base-node";

export const NumericComparisonSchema = z.enum([
  "equals",
  "notEquals",
  "greaterThan",
  "lessThan",
  "greaterThanOrEquals",
  "lessThanOrEquals",
]);

const BaseNodeConditionSchema = z.object({
  id: z.string(),
  variablePath: z.string(),
});

export const ConditionNodeLengthLeafSchema = BaseNodeConditionSchema.extend({
  type: z.literal("length"),
  operator: NumericComparisonSchema,
  comparisonValue: z.number(),
});

export const ConditionNodeValueLeafSchema = BaseNodeConditionSchema.extend({
  type: z.literal("value"),
  comparator: z.enum(["string", "number", "boolean"]),
  operator: NumericComparisonSchema,
  comparisonValue: JSONValueSchema,
});

export const ConditionLeafSchema = z.discriminatedUnion("type", [
  ConditionNodeValueLeafSchema,
  ConditionNodeLengthLeafSchema,
]);

export type ConditionTree = {
  type: "tree";
  operation: "AND" | "OR";
  conditions: ConditionBranch[];
};

export const ConditionTreeSchema: z.ZodSchema<ConditionTree> = z.lazy(() =>
  z.object({
    type: z.literal("tree"),
    operation: z.enum(["AND", "OR"]),
    conditions: z.array(ConditionBranchSchema),
  })
);

export const ConditionBranchSchema = z.union([
  ConditionLeafSchema,
  ConditionTreeSchema,
]);

export const ConditionNodeConfigSchema = BaseNodeDefinitionConfigSchema.extend({
  conditionTree: ConditionTreeSchema,
  trueNodeId: z.string(),
  falseNodeId: z.string(),
});

export const ConditionNodeDefinitionSchema = BaseNodeDefinitionSchema.extend({
  type: z.literal("condition"),
  config: ConditionNodeConfigSchema,
});

export const ConditionNodeResultSchema = BaseNodeExecutionResultSchema.extend({
  type: z.literal("condition"),
  updatedContext: AiNodeContextSchema,
});

export type NumericComparison = z.infer<typeof NumericComparisonSchema>;
export type ConditionNodeLengthLeaf = z.infer<
  typeof ConditionNodeLengthLeafSchema
>;
export type ConditionNodeValueLeaf = z.infer<
  typeof ConditionNodeValueLeafSchema
>;
export type ConditionLeaf = ConditionNodeLengthLeaf | ConditionNodeValueLeaf;

export type ConditionBranch = z.infer<typeof ConditionBranchSchema>;
export type ConditionNodeConfig = z.infer<typeof ConditionNodeConfigSchema>;
export type ConditionNodeDefinition = z.infer<
  typeof ConditionNodeDefinitionSchema
>;
export type ConditionNodeResult = z.infer<typeof ConditionNodeResultSchema>;
